'use client'

import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { captureTrackedButton, captureTrackedLink } from '@/lib/analytics'

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const isInitialPageview = useRef(true)

  useEffect(
    () => {
      if (!posthog.__loaded) {

        const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY
        const POSTHOG_REGION = process.env.NEXT_PUBLIC_POSTHOG_HOST

        if (POSTHOG_API_KEY && POSTHOG_REGION) {
          posthog.init(POSTHOG_API_KEY, {
            api_host: POSTHOG_REGION,
            autocapture: true,
            capture_pageview: true,
            capture_pageleave: true,
            cross_subdomain_cookie: true,
            disable_session_recording: true,
            persistence: 'localStorage+cookie',
            person_profiles: 'identified_only',
            session_recording: {
              maskAllInputs: true,
            },
            loaded: (client) => {
              // Session recording stays off on marketing pages — enable only
              // after explicit consent to keep landing JS light (no rrweb).
              if (process.env.NODE_ENV === 'development') {
                console.log('PostHog loaded successfully')
              }
            }
          })
        }
      }
    },
    []
  )

  useEffect(() => {
    if (isInitialPageview.current) {
      isInitialPageview.current = false
      return
    }

    if (posthog.__loaded) {
      posthog.capture('$pageview', { $current_url: window.location.href })
    }
  }, [pathname])

  useEffect(() => {
    const captureLink = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return

      const link = event.target.closest('a')
      if (link instanceof HTMLAnchorElement) {
        captureTrackedLink(link)
        return
      }

      const button = event.target.closest('button')
      if (button instanceof HTMLButtonElement) captureTrackedButton(button)
    }

    document.addEventListener('click', captureLink, true)
    return () => document.removeEventListener('click', captureLink, true)
  }, [])

  return (
    <PHProvider client={posthog}>
      {children}
    </PHProvider>
  )
}
